/**
 * ProductBrowsePage - Story 7.2: Create Product Browse/Listing Page
 * Public marketplace product browsing page with filters, search, sorting, and pagination
 *
 * Features:
 * - Product grid with responsive layout (2 cols mobile, 3 tablet, 4 desktop)
 * - Advanced filtering: Category, State, District
 * - Search functionality with debouncing
 * - Multiple sort options: Newest, Price Low-High, Price High-Low
 * - Pagination (20 products per page)
 * - Loading skeletons, error states, empty states
 * - Mobile-responsive sidebar with toggle
 * - SEO optimized with meta tags
 *
 * This page is PUBLIC and does not require authentication.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ProductGrid } from '../components/ProductGrid';
import { FilterSidebar } from '../components/FilterSidebar';
import { SortDropdown } from '../components/SortDropdown';
import { usePublicProductFilters } from '../hooks/usePublicProductFilters';
import { fetchProductsWithFilters } from '../services/marketplacePublicService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Menu, Search, X } from 'lucide-react';
import type { ProductBrowsePageProps, LocationOption } from '../types/publicMarketplace.types';

// ============================================================================
// Constants
// ============================================================================

/**
 * Page metadata
 */
const PAGE_TITLE = 'Browse Products - SAUBHAGYA Marketplace';
const PAGE_DESCRIPTION =
  'Browse all biogas products, equipment, seeds, fertilizers, and biomass from verified sellers across India.';
const PAGE_KEYWORDS =
  'biogas marketplace, browse products, biogas equipment, seeds, fertilizer, biomass, green energy';

/**
 * Products per page for pagination
 */
const PAGE_SIZE = 20;

/**
 * Indian states and districts (fallback data)
 * This can be replaced with API-fetched data if available
 */
const STATES_DISTRICTS: Record<string, string[]> = {
  'Andhra Pradesh': ['Visakhapatnam', 'Krishna', 'Guntur', 'Nellore', 'Prakasam'],
  'Bihar': ['Patna', 'East Champaran', 'West Champaran', 'Bhagalpur', 'Madhubani'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Junagadh'],
  'Haryana': ['Faridabad', 'Gurgaon', 'Hisar', 'Rohtak', 'Panipat'],
  'Jharkhand': ['Ranchi', 'Dhanbad', 'Giridih', 'Deoghar', 'East Singhbhum'],
  'Karnataka': ['Bangalore', 'Mysore', 'Belgaum', 'Mangalore', 'Tumkur'],
  'Kerala': ['Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Thrissur', 'Ernakulam'],
  'Madhya Pradesh': ['Indore', 'Bhopal', 'Jabalpur', 'Gwalior', 'Ujjain'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Aurangabad', 'Nashik'],
  'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Sambalpur', 'Berhampur'],
  'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
  'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Ajmer', 'Bikaner'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Salem', 'Tiruchirappalli'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam', 'Karimnagar'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol'],
};

// ============================================================================
// Component
// ============================================================================

/**
 * ProductBrowsePage Component
 * Main landing page for browsing marketplace products with advanced filtering
 */
export const ProductBrowsePage: React.FC<ProductBrowsePageProps> = ({
  className,
  onProductClick: onProductClickProp,
}) => {
  // ============================================================================
  // Hooks
  // ============================================================================

  const navigate = useNavigate();
  const { filters, setSearch, setCategory, setState, setDistrict, setSortBy, goToPage, resetFilters } =
    usePublicProductFilters();

  // ============================================================================
  // State
  // ============================================================================

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [localSearchInput, setLocalSearchInput] = useState(filters.search || '');

  // ============================================================================
  // Computed Values
  // ============================================================================

  /**
   * Get available states
   */
  const states: LocationOption[] = useMemo(() => {
    return Object.keys(STATES_DISTRICTS).map((state) => ({
      value: state,
      label: state,
    }));
  }, []);

  /**
   * Get districts for selected state
   */
  const districts: LocationOption[] = useMemo(() => {
    if (!filters.state) return [];
    const stateDistricts = STATES_DISTRICTS[filters.state] || [];
    return stateDistricts.map((district) => ({
      value: district,
      label: district,
    }));
  }, [filters.state]);

  // ============================================================================
  // Fetch Products
  // ============================================================================

  const { data: productsData, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['public-products', filters],
    queryFn: () =>
      fetchProductsWithFilters({
        search: filters.search,
        category: filters.category || null,
        state: filters.state || null,
        district: filters.district || null,
        sortBy: filters.sortBy || 'newest',
        page: filters.page,
        pageSize: PAGE_SIZE,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true,
  });

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Scroll to top on page load
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle search input change
   */
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchInput(value);
    setSearch(value);
  }, [setSearch]);

  /**
   * Handle search clear
   */
  const handleSearchClear = useCallback(() => {
    setLocalSearchInput('');
    setSearch('');
  }, [setSearch]);

  /**
   * Handle product card click
   */
  const handleProductClick = useCallback(
    (productId: string) => {
      if (onProductClickProp) {
        onProductClickProp(productId);
      } else {
        navigate(`/marketplace/products/${productId}`);
      }
    },
    [navigate, onProductClickProp]
  );

  /**
   * Handle filter changes
   */
  const handleFiltersChange = useCallback(
    (changedFilters: Record<string, any>) => {
      if ('category' in changedFilters) {
        setCategory(changedFilters.category);
      }
      if ('state' in changedFilters) {
        setState(changedFilters.state);
      }
      if ('district' in changedFilters) {
        setDistrict(changedFilters.district);
      }
    },
    [setCategory, setState, setDistrict]
  );

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback(
    (sortBy: 'newest' | 'price_low' | 'price_high') => {
      setSortBy(sortBy);
    },
    [setSortBy]
  );

  /**
   * Handle pagination
   */
  const handlePageChange = useCallback(
    (page: number) => {
      goToPage(page);
    },
    [goToPage]
  );

  /**
   * Handle retry
   */
  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  /**
   * Handle reset filters
   */
  const handleResetFilters = useCallback(() => {
    resetFilters();
    setLocalSearchInput('');
  }, [resetFilters]);

  // ============================================================================
  // Derived Data
  // ============================================================================

  const products = productsData?.data || [];
  const totalPages = productsData?.pagination?.totalPages || 1;
  const currentPage = productsData?.pagination?.page || 0;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content={PAGE_KEYWORDS} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* Main Content */}
      <main
        className={cn('min-h-screen w-full bg-white', className)}
        role="main"
        id="main-content"
      >
        {/* Breadcrumb */}
        <div className="w-full border-b bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
              <button
                onClick={() => navigate('/public/marketplace')}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Marketplace
              </button>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700 font-medium">Products</span>
            </nav>
          </div>
        </div>

        {/* Page Header */}
        <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Browse Products
            </h1>
            <p className="text-lg text-gray-600">
              {productsData?.pagination?.totalItems || 0} products available
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="w-full bg-white border-b sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="relative flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search products by name, category, location..."
                  value={localSearchInput}
                  onChange={handleSearchChange}
                  className="pl-10 pr-10 h-11"
                  aria-label="Search products"
                />
                {localSearchInput && (
                  <button
                    onClick={handleSearchClear}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Mobile Filter Toggle */}
              <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="md:hidden">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle filters</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterSidebar
                      filters={filters}
                      onFiltersChange={handleFiltersChange}
                      states={states}
                      districts={districts}
                      isLoading={isLoading}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-8">
            {/* Sidebar - Desktop Only */}
            <aside className="hidden md:block w-64 flex-shrink-0">
              <div className="sticky top-20">
                <FilterSidebar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  states={states}
                  districts={districts}
                  isLoading={isLoading}
                  className="bg-gray-50 p-6 rounded-lg border"
                />
              </div>
            </aside>

            {/* Products Section */}
            <div className="flex-1 min-w-0">
              {/* Sort Bar */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div className="text-sm text-gray-600">
                  Showing{' '}
                  <span className="font-semibold">
                    {products.length > 0 ? (currentPage * PAGE_SIZE) + 1 : 0}
                  </span>
                  -
                  <span className="font-semibold">
                    {Math.min((currentPage + 1) * PAGE_SIZE, productsData?.pagination?.totalItems || 0)}
                  </span>
                  {' '}of{' '}
                  <span className="font-semibold">
                    {productsData?.pagination?.totalItems || 0}
                  </span>
                  {' '}products
                </div>

                <div className="flex items-center gap-4">
                  <SortDropdown
                    value={filters.sortBy || 'newest'}
                    onChange={handleSortChange}
                  />

                  {(filters.search || filters.category || filters.state || filters.district) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResetFilters}
                      className="text-xs"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              {/* Product Grid */}
              <ProductGrid
                products={products}
                isLoading={isLoading}
                isError={isError}
                error={error ? { message: error.message } : undefined}
                onProductClick={handleProductClick}
                onRetry={handleRetry}
              />

              {/* Pagination */}
              {totalPages > 1 && !isLoading && products.length > 0 && (
                <div className="mt-12 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      {currentPage > 0 && (
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="cursor-pointer"
                          />
                        </PaginationItem>
                      )}

                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                        const pageNum = i;
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={() => handlePageChange(pageNum)}
                              isActive={currentPage === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum + 1}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      {totalPages > 5 && currentPage < totalPages - 1 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}

                      {currentPage < totalPages - 1 && (
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="cursor-pointer"
                          />
                        </PaginationItem>
                      )}
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ProductBrowsePage;
